import store from '../redux/store';

class AccountingDataService {
  // Student Finance Data - based on actual data structure
  async getStudentFinanceData(schoolID, startDate, endDate, students) {
    const start = startDate;
    const end = endDate;

    // 1) Filter students only by school (NOT by createdAt date)
    const filteredStudents = students.filter(student => {
      const sid = student.schoolID || student.schoolId || student.schoolid || student.school_id;
      const matchesSchool = !schoolID || sid === schoolID;
      return matchesSchool;
    });

    let totalFees = 0;
    let paidFees = 0;
    let outstandingFees = 0;

    filteredStudents.forEach(student => {
      if (!Array.isArray(student.finance) || student.finance.length === 0) {
        return;
      }

      // Use the latest finance record for totals/outstanding (matches your structure)
      const latestFinance = student.finance[student.finance.length - 1];

      // Total fees = totalfees + Arrears + othercharges
      const totalFeesAmount = parseFloat(latestFinance.totalfees) || 0;
      const arrears = parseFloat(latestFinance.Arrears) || 0;
      const othercharges = parseFloat(latestFinance.othercharges) || 0;
      const totalStudentFees = totalFeesAmount + arrears + othercharges;
      totalFees += totalStudentFees;

      // 2) Sum payments in the selected period, using payment.date and "School Fees" description
      if (Array.isArray(latestFinance.payment) && latestFinance.payment.length > 0) {
        const totalPaidInPeriod = latestFinance.payment.reduce((sum, payment) => {
          const payDate = new Date(payment.date || payment.createdAt || 0);
          const inRange = payDate >= start && payDate <= end;

          const des = (payment.des || '').toLowerCase();
          const isSchoolFees =
            des.includes('school fee') || des === 'school fees' || des === 'school fee';

          if (inRange && isSchoolFees && des !== 'discount') {
            return sum + (parseFloat(payment.amountpaid) || 0);
          }
          return sum;
        }, 0);

        paidFees += totalPaidInPeriod;
      }

      // 3) Outstanding balance from latest finance record
      const balance = parseFloat(latestFinance.balance) || 0;
      outstandingFees += balance;
    });

    return {
      students: filteredStudents,
      totalFees,
      paidFees,
      outstandingFees
    };
  }

  // Canteen Fees Data - based on actual data structure
  async getCanteenFeesData(schoolID, startDate, endDate, students) {
    const start = startDate;
    const end = endDate;

    // Filter students by schoolID (NOT by createdAt date)
    const filteredStudents = students.filter(student => {
      // Check schoolID with multiple possible field names
      const sid = student.schoolID || student.schoolId || student.schoolid || student.school_id;
      const matchesSchool = !schoolID || sid === schoolID;

      return matchesSchool;
    });

    let totalRevenue = 0;
    let payments = [];
    let outstandingAmount = 0;

    filteredStudents.forEach(student => {
      // 1) Use the dedicated canteen array (new structure)
      if (student.canteen && Array.isArray(student.canteen)) {
        student.canteen.forEach(canteenRecord => {
          const canteenDate = new Date(canteenRecord.date || canteenRecord.createdAt || 0);
          const inRange = canteenDate >= start && canteenDate <= end;

          if (inRange) {
            const amount = parseFloat(canteenRecord.amount) || 0;
            const status = (canteenRecord.status || '').toLowerCase();

            if (status === 'paid') {
              // Fully paid canteen charge in this period
              totalRevenue += amount;
            } else {
              // Not paid or partially paid – treat as outstanding
              outstandingAmount += amount;
            }

            payments.push({
              studentId: student._id,
              studentName: `${student.lastname || ''} ${student.othernames || ''}`.trim(),
              amount,
              date: canteenRecord.date,
              status: canteenRecord.status || 'Unknown',
              description: canteenRecord.description || ''
            });
          }
        });
      }

      // 2) (Optional) Also scan finance.payment for canteen-related payments, if still used anywhere
      if (student.finance && Array.isArray(student.finance) && student.finance.length > 0) {
        student.finance.forEach(finance => {
          if (finance.payment && Array.isArray(finance.payment) && finance.payment.length > 0) {
            finance.payment.forEach(payment => {
              const payDate = new Date(payment.date || payment.createdAt || 0);
              const inRange = payDate >= start && payDate <= end;

              const des = (payment.des || '').toLowerCase();
              const isCanteen =
                des.includes('canteen') || des === 'canteen' || des === 'canteen fees';

              if (inRange && isCanteen) {
                const amount = parseFloat(payment.amountpaid) || 0;
                totalRevenue += amount;
                payments.push({
                  studentId: student._id,
                  studentName: `${student.lastname || ''} ${student.othernames || ''}`.trim(),
                  amount,
                  date: payment.date,
                  status: 'Paid',
                  description: payment.des
                });
              }
            });
          }
        });
      }
    });

    return {
      payments,
      totalRevenue,
      outstandingAmount
    };
  }

  // Salaries Data - based on actual data structure
  async getSalariesData(schoolID, startDate, endDate, teachers) {
    // Filter teachers by schoolID and date range
    const filteredTeachers = teachers.filter(teacher => {
      const teacherDate = new Date(teacher.createdAt || teacher.date || 0);
      const inDateRange = teacherDate >= startDate && teacherDate <= endDate;

      // Check schoolID with multiple possible field names
      const sid = teacher.schoolID || teacher.schoolId || teacher.schoolid || teacher.school_id;
      const matchesSchool = !schoolID || sid === schoolID;

      return inDateRange && matchesSchool;
    });

    let totalSalaries = 0;
    const departmentBreakdown = {};

    filteredTeachers.forEach(teacher => {
      const salary = parseFloat(teacher.salary) || 0;
      totalSalaries += salary;
      
      const department = teacher.department || teacher.position || 'General';
      departmentBreakdown[department] = (departmentBreakdown[department] || 0) + salary;
    });

    return {
      teachers: filteredTeachers,
      totalSalaries,
      departmentBreakdown
    };
  }

  // Expenditure Data - based on actual data structure
  async getExpenditureData(schoolID, startDate, endDate, expenses) {
    // Filter expenses by schoolID and date range
    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date || expense.createdAt || 0);
      const inDateRange = expenseDate >= startDate && expenseDate <= endDate;

      // Check schoolID with multiple possible field names
      const sid = expense.schoolID || expense.schoolId || expense.schoolid || expense.school_id;
      const matchesSchool = !schoolID || sid === schoolID;

      return inDateRange && matchesSchool;
    });

    let totalExpenses = 0;
    const categoryBreakdown = {};

    filteredExpenses.forEach(expense => {
      // Use amountspent to match the rest of your codebase
      const amount = parseFloat(expense.amountspent || expense.amount) || 0;
      totalExpenses += amount;
      
      const category = expense.category || expense.description || expense.purpose || 'Other';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + amount;
    });

    return {
      expenses: filteredExpenses,
      totalExpenses,
      categoryBreakdown
    };
  }

  // Sales Management Data - based on actual data structure
  async getSalesData(schoolID, startDate, endDate, sales) {
    const start = startDate;
    const end = endDate;

    // Filter sales by schoolID (if available) and date range (createdAt)
    const filteredSales = sales.filter(sale => {
      const saleDate = new Date(sale.createdAt || sale.date || 0);
      const inDateRange = saleDate >= start && saleDate <= end;

      // Check schoolID with multiple possible field names
      const sid = sale.schoolID || sale.schoolId || sale.schoolid || sale.school_id;
      const matchesSchool = !schoolID || sid === schoolID;

      return inDateRange && matchesSchool;
    });

    let totalRevenue = 0;
    const productBreakdown = {};

    filteredSales.forEach(sale => {
      // Only count actually PAID sales
      const status = (sale.paymentStatus || '').toLowerCase();
      if (status !== 'paid') {
        return;
      }

      // Use total as the cash amount received (matches your structure)
      const saleTotal = parseFloat(sale.total) || 0;
      totalRevenue += saleTotal;
      
      // Break down by products if items exist
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const product = item.productName || item.name || 'Unknown';
          const itemTotal = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
          productBreakdown[product] = (productBreakdown[product] || 0) + itemTotal;
        });
      }
    });

    return {
      sales: filteredSales,
      totalRevenue,
      productBreakdown
    };
  }

  // Asset Management Data - based on actual data structure
  async getAssetsData(schoolID, startDate, endDate, assets) {
    // Filter assets by schoolID and date range
    // Note: For balance sheet, we might want to include all assets regardless of purchase date
    // but filter by schoolID. Adjust the date filter logic as needed.
    const filteredAssets = assets.filter(asset => {
      const assetDate = new Date(asset.purchaseDate || asset.createdAt || 0);
      // Include assets purchased before or during the period (they still exist)
      const inDateRange = assetDate <= endDate;

      // Check schoolID with multiple possible field names
      const sid = asset.schoolID || asset.schoolId || asset.schoolid || asset.school_id;
      const matchesSchool = !schoolID || sid === schoolID;

      return inDateRange && matchesSchool;
    });

    let totalValue = 0;
    const categoryBreakdown = {};

    filteredAssets.forEach(asset => {
      // Use currentValue if available, otherwise use purchaseCost
      const value = parseFloat(asset.currentValue) || parseFloat(asset.purchaseCost) || 0;
      totalValue += value;
      
      const category = asset.assetType || asset.category || 'Other';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + value;
    });

    return {
      assets: filteredAssets,
      totalValue,
      categoryBreakdown
    };
  }

  // Comprehensive Financial Data - combines all data sources
  async getAllFinancialData(schoolID, startDate, endDate, reduxData) {
    try {
      // Add timeout wrapper to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Financial data fetch timed out after 20 seconds')), 20000);
      });

      // Wrap each data fetch with error handling to prevent one failure from blocking all
      const dataPromise = Promise.all([
        this.getStudentFinanceData(schoolID, startDate, endDate, reduxData.students || []).catch(err => {
          console.error('Error fetching student finance data:', err);
          return { students: [], totalFees: 0, paidFees: 0, outstandingFees: 0 };
        }),
        this.getCanteenFeesData(schoolID, startDate, endDate, reduxData.students || []).catch(err => {
          console.error('Error fetching canteen fees data:', err);
          return { students: [], totalRevenue: 0, outstandingAmount: 0 };
        }),
        this.getSalariesData(schoolID, startDate, endDate, reduxData.teachers || []).catch(err => {
          console.error('Error fetching salaries data:', err);
          return { teachers: [], totalSalaries: 0 };
        }),
        this.getExpenditureData(schoolID, startDate, endDate, reduxData.expenses || []).catch(err => {
          console.error('Error fetching expenditure data:', err);
          return { expenses: [], totalExpenses: 0 };
        }),
        this.getSalesData(schoolID, startDate, endDate, reduxData.sales || []).catch(err => {
          console.error('Error fetching sales data:', err);
          return { sales: [], totalRevenue: 0, productBreakdown: {} };
        }),
        this.getAssetsData(schoolID, startDate, endDate, reduxData.assets || []).catch(err => {
          console.error('Error fetching assets data:', err);
          return { assets: [], totalValue: 0, categoryBreakdown: {} };
        })
      ]).then(([studentFinance, canteenFees, salaries, expenditure, sales, assets]) => ({
        studentFinance,
        canteenFees,
        salaries,
        expenditure,
        sales,
        assets,
        // Pass through products from reduxData so inventory can use real stock
        products: Array.isArray(reduxData.products) ? reduxData.products : [],
        timestamp: new Date().toISOString()
      }));

      return await Promise.race([dataPromise, timeoutPromise]);
    } catch (error) {
      console.error('Error fetching all financial data:', error);
      // Return minimal structure instead of throwing to prevent hanging
      return {
        studentFinance: { students: [], totalFees: 0, paidFees: 0, outstandingFees: 0 },
        canteenFees: { students: [], totalRevenue: 0, outstandingAmount: 0 },
        salaries: { teachers: [], totalSalaries: 0 },
        expenditure: { expenses: [], totalExpenses: 0 },
        sales: { sales: [], totalRevenue: 0, productBreakdown: {} },
        assets: { assets: [], totalValue: 0, categoryBreakdown: {} },
        products: [],
        timestamp: new Date().toISOString()
      };
    }
  }
}

export default new AccountingDataService(); 